import React from "react";
import { api } from "@/lib/api";
import { PACKAGES } from "@/data/content";
import { TrendChart } from "@/components/admin/TrendChart";

const EVENT_LABELS = {
  industry_card_click: "Industry Card Clicks",
  industry_page_view: "Industry Page Visits",
  lead_gen_example_view: "Lead Gen Example Views",
  calendly_cta_click: "Calendly CTA Clicks",
};
const EVENT_COLS = Object.entries(EVENT_LABELS).map(([key, label]) => ({ key, label }));
const SERVICE_NAMES = Object.fromEntries(PACKAGES.map((p) => [p.id, p.name]));
const DAY_MS = 86400000;
const PRESETS = [7, 30, 90];

const isoDay = (d) => d.toISOString().slice(0, 10);
const PENDING_REPORT_KEY = "fbd_pending_report";

async function downloadPdf(path, params, filename) {
  const { data } = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
const presetRange = (days) => ({ start: isoDay(new Date(Date.now() - (days - 1) * DAY_MS)), end: isoDay(new Date()) });

function useNoIndex() {
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Admin · Analytics";
    return () => { meta.remove(); document.title = prevTitle; };
  }, []);
}

function Shell({ children }) {
  return (
    <main className="min-h-screen px-6 md:px-12 py-16" data-testid="admin-analytics-page">
      <div className="max-w-7xl mx-auto">{children}</div>
    </main>
  );
}

function SignIn() {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/analytics";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="max-w-md mx-auto text-center tracing-beam p-12 bg-surface border border-white/5 mt-16">
      <div className="overline">Private · Admin</div>
      <h1 className="font-display font-light text-4xl mt-6">Analytics</h1>
      <p className="text-secondary mt-4 text-sm leading-relaxed">Sign in with an authorized administrator account.</p>
      <button onClick={start} className="btn-primary w-full mt-10 justify-center" data-testid="admin-signin-btn">Continue with Google</button>
    </div>
  );
}

async function signOut() {
  await api.post("/auth/logout").catch(() => {});
  window.location.reload();
}

function Denied() {
  return (
    <div className="max-w-md mx-auto text-center p-12 bg-surface border border-white/5 mt-16" data-testid="admin-access-denied">
      <div className="overline">Access Denied</div>
      <p className="text-secondary mt-6 text-sm leading-relaxed">This account is not authorized to view analytics.</p>
      <button onClick={signOut} className="btn-secondary w-full mt-8 justify-center" data-testid="admin-denied-signout">Sign out</button>
    </div>
  );
}

function RangeBar({ range, setRange }) {
  return (
    <div className="flex flex-wrap items-end gap-6 mt-10" data-testid="admin-range-bar">
      {["start", "end"].map((k) => (
        <label key={k} className="flex flex-col gap-2">
          <span className="overline text-[10px]">{k === "start" ? "From" : "To"}</span>
          <input type="date" value={range[k]} max={isoDay(new Date())} onChange={(e) => setRange({ ...range, [k]: e.target.value })}
            className="input-box [color-scheme:dark]" data-testid={`admin-range-${k}`} />
        </label>
      ))}
      <div className="flex gap-3">
        {PRESETS.map((d) => (
          <button key={d} onClick={() => setRange(presetRange(d))} className="btn-secondary !px-4 !py-2 text-xs" data-testid={`admin-preset-${d}`}>
            Last {d} days
          </button>
        ))}
        <button onClick={() => downloadPdf("/admin/analytics/report.pdf", range, `fbad-analytics-${range.start}-to-${range.end}.pdf`)}
          className="btn-primary !px-4 !py-2 text-xs" data-testid="admin-range-pdf">Download PDF</button>
      </div>
    </div>
  );
}

function Kpis({ totals }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
      {EVENT_COLS.map(({ key, label }) => (
        <div key={key} className="editorial-card p-6" data-testid={`admin-kpi-${key}`}>
          <div className="overline text-[10px]">{label}</div>
          <div className="font-display text-4xl mt-3">{totals[key] ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ title, note, cols, rows, rowKey, testid, empty = "No events in this range." }) {
  return (
    <section className="mt-14" data-testid={testid}>
      <h2 className="font-display text-2xl">{title}</h2>
      {note && <p className="text-secondary text-xs mt-2 leading-relaxed max-w-3xl">{note}</p>}
      <div className="mt-5 overflow-x-auto border border-white/5 bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {cols.map((c) => <th key={c.key} className="overline text-[10px] px-4 py-3 whitespace-nowrap">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-6 text-secondary">{empty}</td></tr>}
            {rows.map((r) => (
              <tr key={rowKey(r)} className="border-t border-white/5">
                {cols.map((c) => <td key={c.key} className="px-4 py-3 text-secondary whitespace-nowrap">{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Report({ data }) {
  const industries = [...data.industries].sort((a, b) => b.calendly_cta_click - a.calendly_cta_click || b.total - a.total);
  return (
    <>
      <Kpis totals={data.totals} />
      <p className="text-secondary text-xs mt-4" data-testid="admin-bookings-note">
        Calendly CTA clicks show booking interest only. They are not confirmed appointments.
      </p>
      <TrendChart trend={data.trend} range={data.range} />
      <Table title="Industry Engagement" testid="admin-industry-table" rowKey={(r) => r.industry}
        note={`Ranked by Calendly CTA clicks. Calendly clicks with no known industry: ${data.unattributed_cta_clicks}.`}
        cols={[{ key: "name", label: "Industry" }, ...EVENT_COLS, { key: "total", label: "Total" }]} rows={industries} />
      <Table title="Calendly CTA Clicks by Location" testid="admin-cta-table" rowKey={(r) => r.cta_location}
        cols={[{ key: "cta_location", label: "CTA Location" }, { key: "count", label: "Clicks" }]} rows={data.cta_locations} />
      <Table title="Calendly CTA Clicks by Service" testid="admin-service-table" rowKey={(r) => r.service} note="Pricing card buttons only."
        cols={[{ key: "service", label: "Service", render: (r) => SERVICE_NAMES[r.service] || r.service }, { key: "count", label: "Clicks" }]} rows={data.services} />
      <Table title="UTM Campaigns" testid="admin-campaign-table" rowKey={(r) => `${r.utm_source}|${r.utm_campaign}`}
        cols={[{ key: "utm_source", label: "Source" }, { key: "utm_campaign", label: "Campaign" }, ...EVENT_COLS, { key: "total", label: "Total" }]} rows={data.campaigns} />
      <Table title="Daily Activity" testid="admin-daily-table" rowKey={(r) => r.date}
        cols={[{ key: "date", label: "Date" }, ...EVENT_COLS]} rows={[...data.daily].reverse()} />
      <RecentEvents rows={data.recent} />
    </>
  );
}

function RecentEvents({ rows }) {
  const cols = [
    { key: "created_at", label: "Time", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "event_type", label: "Event", render: (r) => EVENT_LABELS[r.event_type] },
    { key: "industry", label: "Industry" },
    { key: "service", label: "Service", render: (r) => SERVICE_NAMES[r.service] || r.service || "—" },
    { key: "cta_location", label: "CTA Location" },
    { key: "utm_source", label: "Source" },
    { key: "utm_campaign", label: "Campaign" },
    { key: "path", label: "Page" },
  ];
  return <Table title="Recent Events" testid="admin-recent-table" note="Latest 200 anonymous events in this range." cols={cols} rows={rows} rowKey={(r) => r.id} />;
}

function ReportPanel() {
  const [info, setInfo] = React.useState(null);
  const [msg, setMsg] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const load = React.useCallback(() => api.get("/admin/analytics/reports").then(({ data: d }) => setInfo(d)).catch(() => {}), []);
  React.useEffect(() => { load(); }, [load]);
  const sendTest = async () => {
    setSending(true); setMsg("");
    try {
      const { data: d } = await api.post("/admin/analytics/send-test-report");
      setMsg(`Test report sent (${d.period_start} – ${d.period_end}).`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not send test report");
    } finally { setSending(false); }
  };
  const last = info?.reports?.find((r) => r.status === "sent");
  const downloadable = React.useMemo(() => (info?.reports || []).filter((r) => r.status === "sent" && r.report_id).slice(0, 5), [info]);
  return (
    <section className="mt-10 editorial-card p-6 flex flex-wrap items-center justify-between gap-6" data-testid="admin-report-panel">
      <div>
        <div className="overline text-[10px]">Email Reports</div>
        <p className="text-secondary text-sm mt-3">{info ? `${info.schedule} → ${info.recipient}` : "Loading…"}</p>
        <p className="text-secondary text-xs mt-1" data-testid="admin-report-last">
          {last ? `Last sent ${new Date(last.created_at).toLocaleString()} (${last.kind})` : "No reports sent yet."}
        </p>
        {msg && <p className="text-brand text-xs mt-2" data-testid="admin-report-msg">{msg}</p>}
        <ul className="mt-4 space-y-2">
          {downloadable.map((r) => (
            <li key={r.report_id} className="flex items-center gap-4 text-xs text-secondary">
              <span>{r.period_start} – {r.period_end} ({r.kind})</span>
              <button onClick={() => downloadPdf(`/admin/analytics/reports/${r.report_id}/pdf`, {}, `fbad-analytics-${r.period_start}-to-${r.period_end}.pdf`)}
                className="text-brand uppercase tracking-widest hover:opacity-80" data-testid={`admin-report-pdf-${r.report_id}`}>Download PDF</button>
            </li>
          ))}
        </ul>
      </div>
      <button onClick={sendTest} disabled={sending} className="btn-secondary" data-testid="admin-send-test-report">
        {sending ? "Sending…" : "Send Test Report"}
      </button>
    </section>
  );
}

function AlertsPanel() {
  const [alerts, setAlerts] = React.useState(null);
  React.useEffect(() => { api.get("/admin/analytics/alerts").then(({ data: d }) => setAlerts(d.alerts)).catch(() => setAlerts([])); }, []);
  return (
    <section className="mt-6 editorial-card p-6" data-testid="admin-alerts-panel">
      <div className="overline text-[10px]">Meta Campaign Alerts</div>
      <p className="text-secondary text-xs mt-3">Checked hourly. Alert = 3+ Calendly CTA clicks in 24h and 2× the prior 7-day daily average; newer campaigns are flagged once as early engagement.</p>
      <ul className="mt-4 space-y-2 text-xs text-secondary">
        {alerts?.length === 0 && <li>No alerts yet.</li>}
        {(alerts || []).map((a, i) => (
          <li key={`${a.source}|${a.campaign}|${a.created_at}`} data-testid={`admin-alert-${i}`}>
            {new Date(a.created_at).toLocaleString()} · {a.source} / {a.campaign} · {a.kind === "unusual_jump" ? "Unusual jump" : "Early engagement"} · {a.clicks_24h} unique-visitor clicks (24h)
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function AdminAnalyticsPage() {
  useNoIndex();
  const [auth, setAuth] = React.useState(null);
  const [admin, setAdmin] = React.useState(null);
  const [range, setRange] = React.useState(() => presetRange(30));
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    const pending = new URLSearchParams(window.location.search).get("report");
    if (pending) sessionStorage.setItem(PENDING_REPORT_KEY, pending);
  }, []);

  React.useEffect(() => {
    if (auth !== "ok") return;
    const pending = sessionStorage.getItem(PENDING_REPORT_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_REPORT_KEY);
    window.history.replaceState(null, "", "/admin/analytics");
    downloadPdf(`/admin/analytics/reports/${pending}/pdf`, {}, "fbad-analytics-report.pdf").catch(() => setErr("Report PDF not found"));
  }, [auth]);

  React.useEffect(() => {
    api.get("/admin/session")
      .then(({ data: d }) => { setAdmin(d); setAuth("ok"); })
      .catch((e) => setAuth(e?.response?.status === 403 ? "denied" : "signin"));
  }, []);

  React.useEffect(() => {
    if (auth !== "ok" || !range.start || !range.end) return;
    setErr("");
    api.get("/admin/analytics/summary", { params: range })
      .then(({ data: d }) => setData(d))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load analytics"));
  }, [auth, range]);

  if (auth === null) return <Shell><div className="text-secondary text-xs uppercase tracking-widest mt-16">Checking access…</div></Shell>;
  if (auth === "signin") return <Shell><SignIn /></Shell>;
  if (auth === "denied") return <Shell><Denied /></Shell>;
  return (
    <Shell>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="overline">Private · Admin</div>
          <h1 className="font-display font-light text-4xl md:text-5xl mt-4">Industry Engagement</h1>
          <p className="text-secondary text-sm mt-3" data-testid="admin-signed-in-as">Signed in as {admin?.email}</p>
        </div>
        <button onClick={signOut} className="btn-secondary" data-testid="admin-signout-btn">Sign out</button>
      </div>
      <ReportPanel />
      <AlertsPanel />
      <RangeBar range={range} setRange={setRange} />
      {err && <p className="text-brand text-sm mt-6" data-testid="admin-error">{String(err)}</p>}
      {data ? <Report data={data} /> : !err && <div className="text-secondary text-xs uppercase tracking-widest mt-10">Loading…</div>}
    </Shell>
  );
}
